import createReducer from 'lib/create-reducer'
import { queryStringBool } from '../../lib/helpers/search'
import { constants } from './actions'
import type { FetchNotificationItemsPayload, NotificationsQuery } from './actions'
import { matchesCollection } from './selectors'
import saga from './sagas'
import type {
  CollectionNewCount,
  CollectionRef,
  FeedResponse,
  Notification,
  NotificationItem,
  NotificationItemStatus,
  NotificationsState
} from './types'

const defaultState: NotificationsState = {
  notifications: [],
  loading: false,
  error: null,

  notificationItems: [],
  notificationItemsLoading: false,
  notificationItemsLoadingMore: false,
  notificationItemsError: null,
  notificationItemsPage: 1,
  notificationItemsTotal: 0,
  newNotificationItems: 0,
  notificationItemsFilters: {},

  dropDownNotificationItems: [],
  dropDownNotificationItemsLoading: false,
  dropDownNotificationItemsError: null,

  notificationSetStatusLoading: false,
  notificationSetStatusError: null,

  collectionNewCounts: []
}

const notifications = createReducer(
  defaultState,
  {
    [constants.FETCH_NOTIFICATIONS]: state => ({
      ...state,
      loading: true
    }),
    [constants.FETCH_NOTIFICATIONS_SUCCESS]: (state, notifications: Notification[]) => ({
      ...state,
      loading: false,
      notifications
    }),
    [constants.FETCH_NOTIFICATIONS_FAILURE]: state => ({
      ...state,
      loading: false
    }),
    [constants.SET_NOTIFICATION]: state => ({
      ...state,
      loading: true,
      error: null
    }),
    [constants.SET_NOTIFICATION_SUCCESS]: (state, { notification }: { notification: Notification }) => ({
      ...state,
      loading: false,
      notifications: [...state.notifications, notification]
    }),
    [constants.SET_NOTIFICATION_FAILURE]: (state, payload) => ({
      ...state,
      loading: false,
      error: payload
    }),
    [constants.UPDATE_NOTIFICATION]: (state, notification: Notification) => ({
      ...state,
      error: null,
      notifications: state.notifications.map(item => (item.id === notification.id ? notification : item))
    }),
    [constants.UPDATE_NOTIFICATION_SUCCESS]: state => ({
      ...state,
      loading: false
    }),
    [constants.UPDATE_NOTIFICATION_FAILURE]: (state, payload) => ({
      ...state,
      loading: false,
      error: payload
    }),
    [constants.FLUSH_NOTIFICATION_ERROR]: state => ({
      ...state,
      error: null
    }),
    [constants.DELETE_NOTIFICATIONS]: state => ({
      ...state,
      loading: true
    }),
    [constants.DELETE_NOTIFICATIONS_SUCCESS]: (state, ids: number[]) => ({
      ...state,
      loading: false,
      notifications: state.notifications?.filter(({ id }) => !ids.includes(id))
    }),
    [constants.DELETE_NOTIFICATIONS_FAILURE]: state => ({
      ...state,
      loading: false
    }),
    [constants.FETCH_NOTIFICATION_ITEMS]: (state, payload?: FetchNotificationItemsPayload) => {
      // we want to totally reset filters only with the {} payload
      let notificationItemsFilters = {
        ...state.notificationItemsFilters,
        ...payload?.filters
      }
      if (payload?.filters && Object.keys(payload?.filters).length === 0) {
        notificationItemsFilters = {}
      }
      return {
        ...state,
        notificationItemsLoading: true,
        notificationItemsPage: payload?.pageNumber || state.notificationItemsPage,
        notificationItemsFilters
      }
    },
    [constants.FETCH_NOTIFICATION_ITEMS_SUCCESS]: (state, { notificationItems, total }: FeedResponse) => ({
      ...state,
      notificationItemsLoading: false,
      notificationItems: notificationItems || [],
      notificationItemsTotal: total
    }),
    [constants.FETCH_NOTIFICATION_ITEMS_FAILURE]: state => ({
      ...state,
      notificationItemsLoading: false
    }),
    [constants.FETCH_MORE_NOTIFICATION_ITEMS]: state => ({
      ...state,
      notificationItemsLoadingMore: true,
      notificationItemsPage: (state.notificationItemsPage || 1) + 1
    }),
    [constants.FETCH_MORE_NOTIFICATION_ITEMS_SUCCESS]: (state, { notificationItems, total }: FeedResponse) => {
      // De-dupe on the feed's React key so a double-click can't render a row twice.
      const itemKey = (item: NotificationItem) => `${item.notificationID}-${item.id}`
      const seen = new Set(state.notificationItems.map(itemKey))
      const appended = (notificationItems || []).filter(item => !seen.has(itemKey(item)))
      return {
        ...state,
        notificationItemsLoadingMore: false,
        notificationItems: [...state.notificationItems, ...appended],
        notificationItemsTotal: total
      }
    },
    [constants.FETCH_MORE_NOTIFICATION_ITEMS_FAILURE]: state => ({
      ...state,
      notificationItemsLoadingMore: false,
      // Roll back so a retry re-requests the failed page.
      notificationItemsPage: Math.max(1, (state.notificationItemsPage || 1) - 1)
    }),
    [constants.FETCH_DROP_DOWN_NOTIFICATION_ITEMS]: state => ({
      ...state,
      dropDownNotificationItemsLoading: true
    }),
    [constants.FETCH_DROP_DOWN_NOTIFICATION_ITEMS_SUCCESS]: (
      state,
      { notificationItems, newNotifications }: FeedResponse
    ) => ({
      ...state,
      dropDownNotificationItemsLoading: false,
      dropDownNotificationItems: notificationItems || [],
      newNotificationItems: newNotifications
    }),
    [constants.FETCH_DROP_DOWN_NOTIFICATION_ITEMS_FAILURE]: state => ({
      ...state,
      dropDownNotificationItemsLoading: false
    }),
    [constants.SET_NOTIFICATION_ITEMS_STATUS]: (state, payload?: NotificationItemStatus[]) => {
      const newNewCount = Math.max(
        0,
        state.newNotificationItems - (payload?.filter(item => item.status !== 'NEW').length || 0)
      )
      return {
        ...state,
        notificationSetStatusLoading: true,
        notificationSetStatusError: null,
        newNotificationItems: newNewCount
      }
    },
    [constants.SET_NOTIFICATION_ITEMS_STATUS_SUCCESS]: (
      state,
      { notificationItemStatuses }: { notificationItemStatuses: NotificationItemStatus[] }
    ) => {
      const matchesStatus = (notificationItem: NotificationItem) => (status: NotificationItemStatus) =>
        status.id === notificationItem.id && status.notificationID === notificationItem.notificationID

      const notificationItems = state.notificationItems.map(notificationItem => {
        const notificationItemStatus = notificationItemStatuses.find(matchesStatus(notificationItem))
        if (notificationItemStatus) {
          return {
            ...notificationItem,
            ...notificationItemStatus
          }
        }
        return notificationItem
      })

      const dropDownNotificationItems = state.dropDownNotificationItems.map(notificationItem => {
        const notificationItemStatus = notificationItemStatuses.find(matchesStatus(notificationItem))
        if (notificationItemStatus) {
          return {
            ...notificationItem,
            ...notificationItemStatus
          }
        }
        return notificationItem
      })

      return {
        ...state,
        notificationSetStatusLoading: false,
        notificationItems,
        dropDownNotificationItems
      }
    },
    [constants.SET_NOTIFICATION_ITEMS_STATUS_FAILURE]: (state, payload) => ({
      ...state,
      notificationSetStatusLoading: false,
      notificationSetStatusError: payload
    }),
    [constants.MARK_ALL_NOTIFICATION_ITEMS_READ]: state => {
      const markSeen = (item: NotificationItem): NotificationItem =>
        item.status === 'NEW' ? { ...item, status: 'SEEN' } : item
      return {
        ...state,
        notificationSetStatusLoading: true,
        notificationSetStatusError: null,
        newNotificationItems: 0,
        notificationItems: state.notificationItems.map(markSeen),
        dropDownNotificationItems: state.dropDownNotificationItems.map(markSeen)
      }
    },
    [constants.MARK_ALL_NOTIFICATION_ITEMS_READ_SUCCESS]: state => ({
      ...state,
      notificationSetStatusLoading: false
    }),
    [constants.MARK_ALL_NOTIFICATION_ITEMS_READ_FAILURE]: (state, payload) => ({
      ...state,
      notificationSetStatusLoading: false,
      notificationSetStatusError: payload
    }),
    [constants.FETCH_COLLECTION_NEW_COUNTS_SUCCESS]: (state, collectionNewCounts?: CollectionNewCount[]) => ({
      ...state,
      collectionNewCounts: collectionNewCounts || []
    }),
    [constants.MARK_COLLECTION_NOTIFICATIONS_READ]: (
      state,
      { collection, notificationIDs }: { collection: CollectionRef; notificationIDs?: number[] }
    ) => {
      const ids = new Set((notificationIDs || []).map(String))
      const belongs = (item: NotificationItem) => ids.has(String(item.notificationID))
      const markSeen = (item: NotificationItem): NotificationItem =>
        belongs(item) && item.status === 'NEW' ? { ...item, status: 'SEEN' } : item
      const flipped = state.notificationItems.filter(item => belongs(item) && item.status === 'NEW').length
      return {
        ...state,
        notificationSetStatusLoading: true,
        notificationSetStatusError: null,
        notificationItems: state.notificationItems.map(markSeen),
        dropDownNotificationItems: state.dropDownNotificationItems.map(markSeen),
        // A lower bound — only loaded items are countable; the saga refetches to make it exact.
        newNotificationItems: Math.max(0, (state.newNotificationItems || 0) - flipped),
        collectionNewCounts: (state.collectionNewCounts || []).filter(count => !matchesCollection(count, collection))
      }
    },
    [constants.MARK_COLLECTION_NOTIFICATIONS_READ_SUCCESS]: state => ({
      ...state,
      notificationSetStatusLoading: false
    }),
    [constants.MARK_COLLECTION_NOTIFICATIONS_READ_FAILURE]: (state, payload) => ({
      ...state,
      notificationSetStatusLoading: false,
      notificationSetStatusError: payload
    }),
    [constants.HANDLE_QUERY_CHANGE]: (state, query: NotificationsQuery) => ({
      ...state,
      notificationItemsPage: Number(query.page) || 1,
      notificationItemsFilters: {
        ...state.notificationItemsFilters,
        notificationIDs: query?.notificationIDs?.map(item => Number(item)) || [],
        time: {
          startDate: query?.startDate || null,
          endDate: query?.endDate || null
        },
        status: {
          read: queryStringBool(query, 'read') || false,
          new: queryStringBool(query, 'new') || false,
          archived: queryStringBool(query, 'archived', false) || false
        },
        notificationType: {
          citationStatements: queryStringBool(query, 'citationStatements') || false,
          newPublications: queryStringBool(query, 'newPublications') || false
        }
      }
    })
  },
  saga
)

export default notifications
